import { Button, Col, Row } from "react-bootstrap";
import { default as React, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

import {
    createColumnHelper,
} from "@tanstack/react-table";

function OwnerPageOriginList({
    data,
    controlType,
    saveRecord,
    updateTableState,
    user,
    countryCodeOptions,
    ownerid,
}) {

    const dataSet = 'origins';

    // --- TanStack Table Setup ---
    const tableData = data || [];
    const columnHelper = createColumnHelper();
    const createId = useMemo(() => createElementIdFactory("owner-page-origin-list"), []);
    const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);

    const normalizedCountryOptions = useMemo(() => {
        const source = Array.isArray(countryCodeOptions) ? countryCodeOptions : [];
        const mapped = source
            .map((opt) => {
                if (!opt || typeof opt !== "object") {
                    return null;
                }

                const countryName = String(
                    opt.country ??
                    opt.label ??
                    opt.country_name ??
                    opt.name ??
                    ""
                ).trim();

                if (!countryName) {
                    return null;
                }

                const countryCode = String(
                    opt.country_code ??
                    opt.value ??
                    ""
                ).trim();

                return {
                    label: countryName,
                    value: countryName,
                    country_code: countryCode,
                };
            })
            .filter(Boolean);

        // Keep only the first occurrence of each country name.
        const seen = new Set();
        return mapped.filter((opt) => {
            if (seen.has(opt.value)) {
                return false;
            }
            seen.add(opt.value);
            return true;
        });
    }, [countryCodeOptions]);

    const resolveCountryValue = (row = {}) => {
        const directCountry = String(row.country || "").trim();
        if (directCountry) {
            return directCountry;
        }

        const code = String(row.country_code || "").trim();
        if (!code) {
            return "";
        }

        const matchedOption = normalizedCountryOptions.find(
            (opt) => String(opt.country_code || "") === code
        );

        return matchedOption?.value || code;
    };

    const columns = [
        columnHelper.accessor("owner_origin_id", {
            id: "owner_origin_id",
            header: "ID",
            size: 60,
            cell: ({ getValue }) => {
                const value = getValue();
                const isTempId = String(value || "").includes("temp-");
                return isTempId ? "" : value;
            },
        }),
        columnHelper.accessor((row) => resolveCountryValue(row), {
            id: "country",
            header: "Country of Origin",
            cell: EditableCell,
            meta: {
                options: normalizedCountryOptions,
            }
        }),
        columnHelper.accessor("wholly_foreign", {
            id: "wholly_foreign",
            header: "Majority Foreign",
            cell: EditableCell,
            size: 100,
        }),
        columnHelper.accessor("wholly_this_country", {
            id: "wholly_this_country",
            header: "Only This Country",
            cell: EditableCell,
            size: 100,
        }),
        columnHelper.accessor("delete_owner_origin_id", {
            id: "delete_owner_origin_id",
            header: "",
            enableSorting: false,
            cell: ({ row, table }) =>
                <Button type="button" size="sm" variant="danger" onClick={() => table.options.meta.handleDelete({ row })}>
                    Delete
                </Button>,
            size: 65,
        }),
    ];

    // --- Data Fetching and Update Functions ---
    async function addRecord(event) {
        event.preventDefault();

        // Create the new record
        const newRecord = {
            owner_origin_id: `temp-${Date.now()}`,
            owner_id: ownerid,
            owner_name: "",
            country: "",
            country_code: "",
            wholly_foreign: false,
            wholly_this_country: false,
            created_by: user,
            last_edited_by: user,
            new_record: true
        };

        const updatedList = [newRecord, ...(data || [])];

        updateTableState(updatedList, dataSet);
    }

    // --- Render Functions ---
    function renderList(data) {
        const visibleRows = (data || []).filter(d => Boolean(resolveCountryValue(d)));

        if (visibleRows.length === 0) {
            return <div className="p-3 text-muted">No origins found.</div>;
        }

        return (
            <table className="table table-borderless table-sm mt-1">
                <tbody>
                    {visibleRows.map((d, i) => (
                        <tr key={d.owner_origin_id || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td className="py-2">
                                <span style={{ color: '#333', fontSize: '0.8rem', display: 'block' }}>
                                    {resolveCountryValue(d)}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    function renderComponent() {
        return (
            <div className="componentRendering">
                {controlType === "table" && <Row className="show-Container">
                    <Col className="text-right mb-2" >
                        <Button id={createId("add-btn")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
                    </Col>
                </Row>}
                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {controlType === "table"
                            ? <GenericTable
                                columns={columns}
                                data={tableData}
                                section="owner-origin-list"
                                elementIds={tableElementIds}
                                pageSize={PAGE_SIZES.ownerDetailCountryOriginModalTable}
                                enablePagination={true}
                                meta={{
                                    shouldSkipBlurSave: ({ column }) => column?.id === "country",
                                    updateData: (rowIndex, columnId, value) => {
                                        updateTableState(rowIndex, columnId, value, dataSet);
                                    },
                                    handleSave: async ({ row, column, value }) => {
                                        const record = row.original;

                                        if (column?.id === "country") {
                                            const incomingCountryCode =
                                                typeof value === "string" ? value.trim() : "";
                                            if (!incomingCountryCode) {
                                                alert("Please select a Country of Origin before saving.");
                                                return;
                                            }
                                        }

                                        const normalizedValue = column?.id === "country"
                                            ? (typeof value === "string" ? value.trim() : value)
                                            : value;

                                        const matchedCountry = column?.id === "country"
                                            ? normalizedCountryOptions.find(opt => opt.value === normalizedValue)
                                            : null;

                                        try {
                                            await saveRecord(
                                                {
                                                    ...record,
                                                    [column?.id]: normalizedValue,
                                                    ...(column?.id === "country"
                                                        ? {
                                                            country: normalizedValue,
                                                            country_code: matchedCountry?.country_code || record.country_code || "",
                                                        }
                                                        : {}),
                                                },
                                                'origins',
                                                'owner_origin_id'
                                            );
                                        } catch (err) {
                                            console.error("Save failed:", err);
                                        }
                                    },
                                    handleDelete: async ({ row }) => {
                                        const record = row.original;
                                        const isTemp = record.new_record || String(record.owner_origin_id || '').includes('temp');
                                        if (window.confirm("Are you sure you want to delete this record?")) {
                                            if (isTemp) {
                                                const updatedData = tableData.filter(item => item.owner_origin_id !== record.owner_origin_id);
                                                updateTableState(updatedData, dataSet);
                                            } else {
                                                const archivedRecord = {
                                                    ...record,
                                                    archived: true,
                                                    archived_date: new Date().toISOString()
                                                };
                                                try {
                                                    await saveRecord(archivedRecord, dataSet, 'owner_origin_id');
                                                    const updatedData = tableData.filter(item => item.owner_origin_id !== record.owner_origin_id);
                                                    updateTableState(updatedData, dataSet);
                                                } catch (err) {
                                                    console.error("Archive failed:", err);
                                                    alert("Error archiving record.");
                                                }
                                            }
                                        }
                                    },
                                }}
                              />
                            : renderList(data)}
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <div className="OwnerPageOriginList">
            {renderComponent()}
        </div>
    );
}

export default OwnerPageOriginList;